import { useState, useEffect } from "react";
import { apiClient } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Switch } from "./ui/switch";
import { toast } from "sonner";
import { DeleteConfirmDialog } from "./ui/delete-confirm-dialog";
import { Plus, Pencil, Trash2, Sparkles, Loader2, Search } from "lucide-react";
import * as LucideIcons from "lucide-react";

// Common Lucide icons for specialities selection
const ICON_OPTIONS = [
  "Star", "Zap", "Target", "Trophy", "Flame", "Rocket", "Shield", "Swords",
  "Wind", "MoveDown", "ArrowUp", "Hand", "Gauge", "Brain", "Battery", "Move",
  "Spline", "Skull", "MessageCircle", "Heart", "Crown", "Award", "Medal", "Sparkles"
];

const SpecialitiesTab = () => {
  const [specialities, setSpecialities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSpec, setEditingSpec] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, spec: null });

  useEffect(() => {
    fetchSpecialities();
  }, []);

  const fetchSpecialities = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/specialities");
      setSpecialities(response.data);
    } catch (error) {
      toast.error("Failed to fetch specialities");
    } finally {
      setLoading(false);
    }
  };

  const filteredSpecialities = specialities.filter(s => 
    s.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-5 h-5" /> : <Sparkles className="w-5 h-5" />;
  };

  const handleDelete = async () => {
    if (!deleteConfirm.spec) return;
    try {
      await apiClient.delete(`/specialities/${deleteConfirm.spec.id}`);
      toast.success("Speciality deleted");
      setDeleteConfirm({ open: false, spec: null });
      fetchSpecialities();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete speciality");
    }
  };

  // Add Modal Component
  const AddSpecialityModal = () => {
    const [name, setName] = useState("");
    const [icon, setIcon] = useState("Star");
    const [description, setDescription] = useState("");
    const [isActive, setIsActive] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async () => {
      if (!name.trim()) {
        toast.error("Name is required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.post("/specialities", {
          name: name.trim(),
          icon,
          description: description.trim() || null,
          is_active: isActive,
        });
        toast.success("Speciality added");
        setShowAddModal(false);
        fetchSpecialities();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to add speciality");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-[#064E3B]" />
              Add Speciality
            </DialogTitle>
            <DialogDescription>Create a new player speciality tag</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                data-testid="add-spec-name"
                placeholder="e.g., Fast Service"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-6 gap-2 p-2 border rounded-xl max-h-32 overflow-y-auto bg-slate-50">
                {ICON_OPTIONS.map((iconName) => {
                  const IconComp = LucideIcons[iconName];
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setIcon(iconName)}
                      className={`p-2 rounded-lg transition-all ${
                        icon === iconName 
                          ? "bg-[#064E3B] text-white" 
                          : "bg-white hover:bg-slate-100"
                      }`}
                      title={iconName}
                    >
                      {IconComp && <IconComp className="w-5 h-5 mx-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                data-testid="add-spec-desc"
                placeholder="Brief description of this skill"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <Label className="text-sm font-medium">Active</Label>
                <p className="text-xs text-slate-500">Visible to users for selection</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
              data-testid="add-spec-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Speciality"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  // Edit Modal Component
  const EditSpecialityModal = () => {
    const [name, setName] = useState(editingSpec?.name || "");
    const [icon, setIcon] = useState(editingSpec?.icon || "Star");
    const [description, setDescription] = useState(editingSpec?.description || "");
    const [isActive, setIsActive] = useState(editingSpec?.is_active ?? true);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      if (editingSpec) {
        setName(editingSpec.name || "");
        setIcon(editingSpec.icon || "Star");
        setDescription(editingSpec.description || "");
        setIsActive(editingSpec.is_active ?? true);
      }
    }, [editingSpec]);

    const handleSubmit = async () => {
      if (!name.trim()) {
        toast.error("Name is required");
        return;
      }
      setSubmitting(true);
      try {
        await apiClient.put(`/specialities/${editingSpec.id}`, {
          name: name.trim(),
          icon,
          description: description.trim() || null,
          is_active: isActive,
        });
        toast.success("Speciality updated");
        setShowEditModal(false);
        setEditingSpec(null);
        fetchSpecialities();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Failed to update speciality");
      } finally {
        setSubmitting(false);
      }
    };

    return (
      <Dialog open={showEditModal} onOpenChange={(open) => { setShowEditModal(open); if (!open) setEditingSpec(null); }}>
        <DialogContent className="sm:max-w-md rounded-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#064E3B]" />
              Edit Speciality
            </DialogTitle>
            <DialogDescription>Update speciality details</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                data-testid="edit-spec-name"
                placeholder="e.g., Fast Service"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Icon</Label>
              <div className="grid grid-cols-6 gap-2 p-2 border rounded-xl max-h-32 overflow-y-auto bg-slate-50">
                {ICON_OPTIONS.map((iconName) => {
                  const IconComp = LucideIcons[iconName];
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => setIcon(iconName)}
                      className={`p-2 rounded-lg transition-all ${
                        icon === iconName 
                          ? "bg-[#064E3B] text-white" 
                          : "bg-white hover:bg-slate-100"
                      }`}
                      title={iconName}
                    >
                      {IconComp && <IconComp className="w-5 h-5 mx-auto" />}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Input
                data-testid="edit-spec-desc"
                placeholder="Brief description of this skill"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="h-11 rounded-xl"
              />
            </div>
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
              <div>
                <Label className="text-sm font-medium">Active</Label>
                <p className="text-xs text-slate-500">Visible to users for selection</p>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditModal(false); setEditingSpec(null); }} className="rounded-xl">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting}
              className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
              data-testid="edit-spec-submit"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#064E3B]" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <Card className="rounded-2xl border-slate-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-heading flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-[#064E3B]" />
                Player Specialities
              </CardTitle>
              <CardDescription>Manage skill tags that members can add to their profiles</CardDescription>
            </div>
            <Button
              onClick={() => setShowAddModal(true)}
              className="bg-[#064E3B] hover:bg-[#065f46] rounded-xl"
              data-testid="add-speciality-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Speciality
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Search */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search specialities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-10 rounded-xl"
                data-testid="search-specialities"
              />
            </div>
          </div>

          {/* Specialities List */}
          {filteredSpecialities.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Sparkles className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>{searchQuery ? "No matching specialities found" : "No specialities created yet"}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {filteredSpecialities.map((spec) => (
                <div
                  key={spec.id}
                  className={`flex items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                    spec.is_active 
                      ? "bg-white border-slate-200" 
                      : "bg-slate-50 border-slate-200 opacity-60"
                  }`}
                  data-testid={`spec-item-${spec.id}`}
                >
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    spec.is_active ? "bg-[#064E3B]/10 text-[#064E3B]" : "bg-slate-200 text-slate-500"
                  }`}>
                    {getIcon(spec.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-900 truncate">{spec.name}</p>
                    {spec.description && (
                      <p className="text-xs text-slate-500 truncate">{spec.description}</p>
                    )}
                    {!spec.is_active && (
                      <span className="text-xs text-amber-600 font-medium">Inactive</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => { setEditingSpec(spec); setShowEditModal(true); }}
                      className="h-8 w-8 rounded-lg"
                      data-testid={`edit-spec-${spec.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeleteConfirm({ open: true, spec })}
                      className="h-8 w-8 rounded-lg text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-spec-${spec.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <AddSpecialityModal />
      <EditSpecialityModal />
      
      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={deleteConfirm.open}
        onOpenChange={(open) => setDeleteConfirm({ open, spec: deleteConfirm.spec })}
        onConfirm={handleDelete}
        title="Delete Speciality"
        description={`Are you sure you want to delete "${deleteConfirm.spec?.name}"? This will remove it from all user profiles.`}
      />
    </div>
  );
};

export default SpecialitiesTab;
